import { DecimalSchema } from "../../decimal";
import { z } from "zod";

// =====================================
// In-proc DTOs for rewards distribution
// =====================================

// DTO for finished epochs waiting for reward processing
export const CashbackFinishedEpochSchema = z.object({
  epochId: z.number().int().positive(),
});

export type CashbackFinishedEpoch = z.infer<typeof CashbackFinishedEpochSchema>;

// DTO for eligible users in an epoch (for merkle tree generation)
// Note: hubUserId is internal only, not exposed in DTO
export const CashbackEligibleUserSchema = z.object({
  defiAddress: z.string(),
  rewardAmount: z.string(), // Total cashback amount in USD (as string for precision)
});

export type CashbackEligibleUser = z.infer<typeof CashbackEligibleUserSchema>;

// =====================================
// API Response DTOs
// =====================================

// Epoch detail schema for claimable/claimableSoon breakdown
export const CashbackEpochDetailSchema = z.object({
  epochId: z.number().int().positive(),
  amount: z.string(),
  cooldownEnd: z.string().optional(), // ISO date string, only for claimableSoon
});

export type CashbackEpochDetail = z.infer<typeof CashbackEpochDetailSchema>;

// Pending cashback transaction with order details
export const PendingCashbackTransactionSchema = z.object({
  id: z.string(),
  exchangeId: z.string(),
  tradingPair: z.string(),
  cashbackAmount: z.string(),
  createdAt: z.string(),
});

export type PendingCashbackTransaction = z.infer<typeof PendingCashbackTransactionSchema>;

// GET /cashback/status response
export const CashbackStatusResponseSchema = z.object({
  accumulated: z.object({
    amount: z.string(), // Decimal as string
    currency: z.string(),
  }),
  awaitingDistribution: z
    .object({
      amount: z.string(), // Cashback assigned to epoch but not yet distributed on-chain
      currency: z.string(),
    })
    .nullable(),
  claimable: z
    .object({
      amount: z.string(),
      currency: z.string(),
      epochId: z.number().int().positive(),
      epochs: z.array(CashbackEpochDetailSchema), // Detail by epoch
    })
    .nullable(),
  claimableSoon: z
    .object({
      amount: z.string(),
      currency: z.string(),
      epochId: z.number().int().positive(),
      cooldownEnd: z.string(), // ISO date string (earliest)
      epochs: z.array(CashbackEpochDetailSchema), // Detail by epoch
    })
    .nullable(),
  monthlyUsage: z.object({
    used: z.string(),
    claimed: z.string(),
    cap: z.string(),
    remaining: z.string(),
    month: z.string(), // Format: "2025-01"
  }),
  unlockProgress: z.object({
    current: z.string(),
    threshold: z.string(),
    percentage: z.number().min(0).max(100),
    pendingTransactions: z.array(PendingCashbackTransactionSchema),
  }),
});

export type CashbackStatusResponse = z.infer<typeof CashbackStatusResponseSchema>;

// GET /cashback/config response
export const CashbackConfigResponseSchema = z.object({
  cashbackRate: DecimalSchema,
  unlockThresholdUsd: DecimalSchema,
  monthlyCapUsd: DecimalSchema,
  isActive: z.boolean(),
  enabledExchanges: z.array(z.string()),
});

export type CashbackConfigResponse = z.infer<typeof CashbackConfigResponseSchema>;

// =====================================
// CMS DTOs (Solo CMS format - NO attributes wrapper)
// =====================================

// Per-exchange rate overrides schema (e.g., {"binance": 0.30, "cryptocom": null})
export const ExchangeRateOverridesSchema = z.record(z.string(), z.number().nullable());
export type ExchangeRateOverrides = z.infer<typeof ExchangeRateOverridesSchema>;

// Global config from CMS
export const CashbackConfigDtoSchema = z.object({
  id: z.string(),
  cashbackRate: z.number(), // e.g., 0.25 for 25%
  unlockThresholdUsd: z.number(), // e.g., 5 - GLOBAL threshold
  monthlyCapUsd: z.number(), // e.g., 500 - GLOBAL cap
  isActive: z.boolean(),
  enabledExchanges: z.array(z.string()), // e.g., ["cryptocom"]
  enabledContexts: z.object({
    tradePage: z.boolean(),
    campaigns: z.boolean(),
  }),
  exchangeRateOverrides: ExchangeRateOverridesSchema.optional().default({}), // e.g., {"binance": 0.30}
  vaultAlertEnabled: z.boolean(), // Enable/disable low funds alert for cashback vault
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CashbackConfigDto = z.infer<typeof CashbackConfigDtoSchema>;

// Per-campaign override (optional)
export const CampaignCashbackConfigDtoSchema = z.object({
  campaignName: z.string(),
  cashbackEnabled: z.boolean(),
  cashbackRateOverride: z.number().nullable(), // null = use global rate
});

export type CampaignCashbackConfigDto = z.infer<typeof CampaignCashbackConfigDtoSchema>;

// =====================================
// Admin DTOs
// =====================================

// POST /admin/cashback/config request body
export const UpdateCashbackConfigDtoSchema = z.object({
  id: z.string(),
  cashbackRate: z.number().min(0).max(1), // e.g., 0.25 for 25%
  unlockThresholdUsd: z.number().positive(),
  monthlyCapUsd: z.number().positive(),
  isActive: z.boolean(),
  enabledExchanges: z.array(z.string()),
  enabledContexts: z.object({
    tradePage: z.boolean(),
    campaigns: z.boolean(),
  }),
  exchangeRateOverrides: ExchangeRateOverridesSchema.optional(), // e.g., {"binance": 0.30}
  vaultAlertEnabled: z.boolean(), // Enable/disable low funds alert for cashback vault
});

export type UpdateCashbackConfigDto = z.infer<typeof UpdateCashbackConfigDtoSchema>;

// GET /admin/cashback/config response (includes all fields for admin)
export const AdminCashbackConfigResponseSchema = z.object({
  id: z.string(),
  cashbackRate: z.number(),
  unlockThresholdUsd: z.number(),
  monthlyCapUsd: z.number(),
  isActive: z.boolean(),
  enabledExchanges: z.array(z.string()),
  enabledContexts: z.object({
    tradePage: z.boolean(),
    campaigns: z.boolean(),
  }),
  exchangeRateOverrides: ExchangeRateOverridesSchema, // e.g., {"binance": 0.30}
  vaultAlertEnabled: z.boolean(), // Enable/disable low funds alert for cashback vault
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AdminCashbackConfigResponse = z.infer<typeof AdminCashbackConfigResponseSchema>;

export const SaveCashbackConfigResponseSchema = z.object({
  id: z.string(),
  message: z.string(),
});

export type SaveCashbackConfigResponse = z.infer<typeof SaveCashbackConfigResponseSchema>;
